import "./app.less";

import { MessageDescriptor } from "@lingui/core";
import { msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ContextMenuLink,
  Page,
  Spinner,
  VisibleChangeFunction,
} from "@ster/ster-toolkit";
import { App as AntApp } from "antd";
import { isBefore, isSameDay } from "date-fns";
import Cookies from "js-cookie";
import { ReactNode, memo, useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";

import { ApplicationFromSterInlog, TenantEnum } from "../api";
import { ClaimType, RoleType } from "../shared/models";
import { appSelector } from "../shared/selectors";
import {
  useAccountQuery,
  useGetApiV1CampaignForwardAvailableQuery,
  useGetTermsAndConditionsQuery,
} from "../store/coreApi";
import { invalidate } from "../store/generic/actions";
import { setOperationalContextFromAccount } from "../store/operationalContextSlice";
import { getLastRead, isAccessTokenValid, setLastRead } from "../utils";
import {
  impersonateCookieName,
  organisationCookieName,
} from "../utils/constants";
import { useAppDispatch } from "../utils/hooks";
import { isAdmin, isImpersonating, isInternalUser } from "../utils/userHelper";
import useAnalytics from "./Analytics";
import { router } from "./AppContainer";
import AppRoutes from "./AppRoutes";
import { loginUrl } from "./authentication/constants";
import ChatwootWidget from "./ChatwootWidget";
import ErrorBoundary from "./ErrorBoundary";
import errorMessages from "./errorMessages";
import Impersonate, { roleLabel } from "./Impersonate";
import AvailableCampaignNotification from "./partials/AvailableCampaignNotification";
import usePortalSettings from "./portalSettings/usePortalSettings";
import ProductTourModal from "./productTour/ProductTourModal";
import Ribbon from "./Ribbon";
import TermsModal from "./terms/TermsModal";

interface NavLink {
  title: MessageDescriptor;
  to: string;
  accessWithClaims?: ClaimType[];
  accessWithRoles?: RoleType[];
  accessWithTenant?: TenantEnum[];
}

const emptyApplications: ApplicationFromSterInlog[] = [];
const emptyNavlinks: NavLink[] = [];

const navlinks: NavLink[] = [
  {
    title: msg`Overzicht`,
    to: "/",
    accessWithRoles: ["User"],
  },
  {
    title: msg`Campagnes`,
    to: "/campaigns",
    accessWithClaims: ["campagnes_tv", "campagnes_radio", "campagnes_inter"],
  },
  {
    title: msg`Analyses`,
    to: "/analysis",
    accessWithClaims: ["analyse"],
    accessWithTenant: [TenantEnum.National],
  },
  {
    title: msg`Facturen`,
    to: "/invoices",
    accessWithClaims: ["facturen_tv", "facturen_radio", "facturen_inter"],
    accessWithTenant: [TenantEnum.National],
  },
  {
    title: msg`Materiaal`,
    to: "/material",
    accessWithClaims: [
      "materialinzien_tv",
      "materialinzien_radio",
      "materialinzien_inter",
    ],
  },
  {
    title: msg`Documenten`,
    to: "/documents",
    accessWithClaims: ["documenten_download"],
    accessWithTenant: [TenantEnum.National],
  },
  {
    title: msg`Beheer`,
    accessWithRoles: ["Admin"],
    to: "/admin",
  },
];

const App = memo(() => {
  useAnalytics();
  const dispatch = useAppDispatch();
  const { message, notification } = AntApp.useApp();
  const { i18n } = useLingui();

  const [showTour, setShowTour] = useState(false);
  const { failures } = useSelector(appSelector);

  const {
    data: account,
    isLoading: loadingAccount,
    isSuccess: loggedIn,
  } = useAccountQuery(undefined, { skip: !isAccessTokenValid() });

  const { settings, isLoading: loadingSettings } = usePortalSettings();
  const { data: terms } = useGetTermsAndConditionsQuery(undefined, {
    skip: !loggedIn,
  });
  const { data: availableCampaigns } = useGetApiV1CampaignForwardAvailableQuery(
    undefined,
    {
      skip: !(loggedIn && isAccessTokenValid()),
      pollingInterval: 300_000,
      skipPollingIfUnfocused: true,
    }
  );

  const loading = loadingAccount || loadingSettings;
  const termsDate = settings.termsDate ?? "";

  const showTerms =
    settings.enableTerms &&
    terms &&
    !isSameDay(new Date(terms.dateOfTerms), new Date(termsDate));

  useEffect(() => {
    if (account) {
      dispatch(setOperationalContextFromAccount(account));
    }
  }, [dispatch, account]);

  const impersonating = isImpersonating();
  const internalUser = isInternalUser(account);
  const admin = isAdmin(account);

  const handleShowTour = useCallback(
    (onVisibleChange: VisibleChangeFunction) => {
      onVisibleChange(false);
      setShowTour(true);
    },
    []
  );

  const renderContextMenu = useCallback(
    (onVisibleChange: VisibleChangeFunction): ReactNode => (
      <>
        {internalUser && (
          <>
            <span>
              {i18n._(t`Bekijk als`)}:{" "}
              <strong>
                {Cookies.get(organisationCookieName) ??
                  account?.userInfo?.organisationCode}
              </strong>
            </span>
            <ContextMenuLink
              className="context-menu__on-behalf-link"
              to={`/organisation?next=${encodeURIComponent(`/campaigns`)}`}
              onVisibleChange={onVisibleChange}
            >
              {i18n._(t`Wijzigen`)}
            </ContextMenuLink>
          </>
        )}
        <Impersonate account={account} onVisibleChange={onVisibleChange} />
        <button
          type="button"
          className="link context-menu__link"
          onClick={(_) => handleShowTour(onVisibleChange)}
        >
          {i18n._(t`Uitleg Klantportal`)}
        </button>
      </>
    ),
    [account, handleShowTour, i18n, internalUser]
  );

  const [lastRead, setLastReadInState] = useState(getLastRead());
  const handleReadAll = useCallback(() => {
    setLastRead();
    setLastReadInState(new Date());
  }, []);

  if (failures && failures.length > 0) {
    failures.forEach(({ propertyName }) => {
      const description = errorMessages[propertyName] ? (
        <>
          {i18n._(errorMessages[propertyName])}{" "}
          {i18n._(errorMessages.defaultMessage)}{" "}
          <a href="mailto:klantportal@ster.nl">klantportal@ster.nl</a>
        </>
      ) : (
        <>
          {i18n._(errorMessages.defaultMessage)}{" "}
          <a href="mailto:klantportal@ster.nl">klantportal@ster.nl</a>
        </>
      );
      notification.error({
        key: `${propertyName}`,
        message: i18n._(t`Fout`),
        description,
        duration: 0,
        onClose: () => {
          dispatch(invalidate(propertyName));
        },
      });
    });
  }

  const navLinks = (
    loggedIn
      ? navlinks.filter(
          (l) =>
            (!l.accessWithRoles ||
              account.roles.some((value: string) =>
                l.accessWithRoles?.includes(value as RoleType)
              )) &&
            (!l.accessWithClaims ||
              account.claims.some((value: string) =>
                l.accessWithClaims?.includes(value as ClaimType)
              )) &&
            (!l.accessWithTenant ||
              (settings?.tenant &&
                l.accessWithTenant.includes(settings.tenant))) &&
            (settings?.enableAnalysis ? true : l.to !== "/analysis")
        )
      : emptyNavlinks
  ).map((n) => ({ ...n, title: i18n._(n.title) }));

  const notifications = availableCampaigns?.map((campaign) => ({
    message: (
      <AvailableCampaignNotification key={campaign.id} campaign={campaign} />
    ),
    isRead: isBefore(campaign.forwardedOn, lastRead),
    clickToClose: true,
  }));

  return (
    <ErrorBoundary
      cloudRoleNameUi={settings.appInsights?.cloudRoleNameUi}
      instrumentationKey={settings.appInsights?.instrumentationKey}
      router={router}
      message={message}
    >
      <Page
        loggedIn={loggedIn}
        navLinks={navLinks}
        applications={account?.sterInlog.applications ?? emptyApplications}
        notifications={notifications}
        onReadAll={handleReadAll}
        extraContextMenuItems={renderContextMenu}
        loginUrl={loginUrl.replace(/\/$/, "")}
        isInternalUser={internalUser}
        isAdmin={admin}
        backLabelText={i18n._(t`Terug naar overzicht`)}
        userContextMenuLabels={{
          websiteLabel: i18n._(t`Ster Website`),
          adminLabel: i18n._(t`Beheer`),
          profileLabel: i18n._(t`Mijn profiel`),
          languageLabel: i18n._(t`Taal / Language`),
          notificationsLabel: i18n._(t`Mijn notificaties`),
          changePasswordLabel: i18n._(t`Wachtwoord wijzigen`),
          logoutLabel: i18n._(t`Uitloggen`),
        }}
        footerLabels={{
          terms: i18n._(t`Voorwaarden`),
          cookieStatement: i18n._(t`Cookiestatement`),
          changeCookieSettings: i18n._(t`Wijzig cookie instellingen`),
          privacyStatement: i18n._(t`Privacystatement`),
        }}
        icon60Years
        applyRegionalStyling={settings.isRegional}
      >
        <Helmet
          defaultTitle="Klantportal - Ster reclame"
          titleTemplate="%s - Klantportal - Ster reclame"
        >
          <html lang="nl" />
        </Helmet>
        {settings && account?.sterInlog && (
          <ChatwootWidget sterInlog={account.sterInlog} settings={settings} />
        )}

        {impersonating && (
          <Ribbon
            text={`${i18n._(t`Huidige rol`)}: ${i18n._(
              roleLabel[Cookies.get(impersonateCookieName) as string]
            )}`}
          />
        )}

        <Spinner spinning={loading}>
          {
            // Lege inhoud om spinner in embed mode te zetten
          }
          {!loading && <AppRoutes account={account} settings={settings} />}
          {showTerms && <TermsModal dateOfTerms={termsDate} />}
          {loggedIn && (
            <ProductTourModal visible={showTour} setVisible={setShowTour} />
          )}
        </Spinner>
      </Page>
    </ErrorBoundary>
  );
});

export default App;
